import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Bell, Check, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Breadcrumb } from "@/components/layout/Breadcrumb";

export default function Reminders() {
  const qc = useQueryClient();
  const { toast } = useToast();

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["reminders"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("scheduled_tasks")
        .select("*")
        .eq("skill_name", "reminder")
        .order("next_run_at", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  const cancel = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("scheduled_tasks").update({ active: false }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reminders"] });
      toast({ title: "Lembrete cancelado" });
    },
  });

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: "Lembretes" }]} />
      <div>
        <h1 className="text-2xl font-bold font-headline flex items-center gap-2"><Bell className="w-6 h-6 text-primary" /> Lembretes</h1>
        <p className="text-sm text-muted-foreground">Lembretes agendados pela SYRA via comando <code>/lembrete</code>.</p>
      </div>

      <Card>
        <CardHeader><CardTitle>{items.length} lembretes</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Nenhum lembrete ativo.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Quando</TableHead>
                  <TableHead>Mensagem</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((r) => {
                  const payload = (r.payload ?? {}) as { message?: string };
                  return (
                    <TableRow key={r.id}>
                      <TableCell className="text-xs">{r.next_run_at ? new Date(r.next_run_at).toLocaleString("pt-BR") : "—"}</TableCell>
                      <TableCell className="text-sm">{payload.message ?? "—"}</TableCell>
                      <TableCell>
                        {r.active ? <Badge><Check className="w-3 h-3 mr-1" /> ativo</Badge> : <Badge variant="secondary"><X className="w-3 h-3 mr-1" /> cancelado</Badge>}
                      </TableCell>
                      <TableCell>
                        {r.active && <Button size="sm" variant="ghost" onClick={() => cancel.mutate(r.id)}>Cancelar</Button>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
